import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Spinner } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import { useBrandContext } from '@/context/brand-context'
import { useInfiniteDesigns, useToggleFavoriteDesign } from '@/api/designs'

import type { Design } from '@/api/designs'
import Lightbox from './components/lightbox'
import GalleryNavbar from './components/gallery-navbar'
import MasonryGrid from './components/masonry-grid'
import SymmetricGrid from './components/symmetric-grid'
import LargeCardsGrid from './components/large-cards-grid'
import { useLayoutMode } from './hooks/use-layout-mode'
import '@/components/ambient/animations.css'

import type { LayoutMode } from './types'

function GalleryPage() {
  const { brandAnalysis } = useBrandContext()
  const [lightboxDesign, setLightboxDesign] = useState<Design | null>(null)

  const { layoutMode, setLayoutMode } = useLayoutMode()
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteDesigns({})
  const { mutate: toggleFavorite } = useToggleFavoriteDesign()

  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const brandName = brandAnalysis?.brand.name ?? 'Brand'
  const brandLogo = brandAnalysis?.brand.logo ?? null
  const brandColor = useMemo(() => {
    const colors = brandAnalysis?.brand.colors ?? []
    return (
      colors.find((c) => c.usage === 'primary')?.hex ?? colors[0]?.hex ?? null
    )
  }, [brandAnalysis?.brand.colors])
  const designs = data?.pages.flatMap((p) => p.items) ?? []

  const handleToggleFavorite = useCallback(
    (designId: string, isFavorite: boolean) => {
      toggleFavorite({ designId, isFavorite })
    },
    [toggleFavorite]
  )

  const handleImageClick = useCallback((design: Design) => {
    setLightboxDesign(design)
  }, [])

  const handleRedesignComplete = useCallback(() => {
    setLightboxDesign(null)
  }, [])

  return (
    <Box position="relative" minH="100vh" bg="bg.primary">
      <Box>
        <GalleryNavbar
          brandName={brandName}
          brandLogo={brandLogo}
          brandColor={brandColor}
          designCount={designs.length}
          layoutMode={layoutMode}
          onChangeLayout={setLayoutMode}
        />

        {/* Layout grid with crossfade */}
        <Box py="40px">
          <AnimatePresence mode="wait">
            <motion.div
              key={layoutMode}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <GridForMode
                layoutMode={layoutMode}
                designs={designs}
                onImageClick={handleImageClick}
                onToggleFavorite={handleToggleFavorite}
              />
            </motion.div>
          </AnimatePresence>

          <Box ref={sentinelRef} h="1px" />

          {isFetchingNextPage && (
            <Box display="flex" justifyContent="center" py="40px">
              <Spinner size="lg" color="text.tertiary" />
            </Box>
          )}
        </Box>
      </Box>

      {lightboxDesign && (
        <Lightbox
          design={lightboxDesign}
          onClose={() => setLightboxDesign(null)}
          onRedesignComplete={handleRedesignComplete}
        />
      )}

    </Box>
  )
}

function GridForMode({
  layoutMode,
  designs,
  onImageClick,
  onToggleFavorite
}: {
  layoutMode: LayoutMode
  designs: Design[]
  onImageClick: (design: Design) => void
  onToggleFavorite: (designId: string, isFavorite: boolean) => void
}) {
  const gridProps = { designs, onImageClick, onToggleFavorite }

  switch (layoutMode) {
    case 'grid':
      return <SymmetricGrid {...gridProps} />
    case 'large':
      return <LargeCardsGrid {...gridProps} />
    default:
      return <MasonryGrid {...gridProps} />
  }
}

export default GalleryPage
